import { supabase } from './supabase';

const BUCKET_NAME = 'project-files';

/**
 * Sanitize filename for safe storage
 */
function sanitizeFileName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .substring(0, 100);
}

/**
 * Upload a file to Supabase Storage
 * Files are stored in user-specific folders: {userId}/projects/{projectId}/files/{filename}
 */
export async function uploadFile(
  file: File,
  projectId: string,
  folder: 'files' | 'images' | 'drawings' = 'files'
): Promise<{ url: string | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { url: null, error: new Error('Kullanıcı girişi gerekli') };
  }

  const safeName = sanitizeFileName(file.name);
  const filePath = `${user.id}/projects/${projectId}/${folder}/${Date.now()}_${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    return { url: null, error: uploadError };
  }

  const { data: { publicUrl } } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(filePath);

  return { url: publicUrl, error: null };
}

/**
 * Upload a base64 image (for canvas drawings)
 */
export async function uploadBase64Image(
  base64Data: string,
  projectId: string,
  fileName: string,
  folder: 'images' | 'drawings' = 'drawings'
): Promise<{ url: string | null; error: Error | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { url: null, error: new Error('Kullanıcı girişi gerekli') };
  }

  // Convert base64 to blob using native fetch API for better performance
  const response = await fetch(base64Data);
  const blob = await response.blob();

  const filePath = `${user.id}/projects/${projectId}/${folder}/${Date.now()}_${fileName}.png`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(filePath, blob, {
      contentType: 'image/png',
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    return { url: null, error: uploadError };
  }

  const { data: { publicUrl } } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(filePath);

  return { url: publicUrl, error: null };
}

/**
 * Delete a file from Supabase Storage
 */
export async function deleteFile(filePath: string): Promise<{ error: Error | null }> {
  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([filePath]);

  return { error };
}

/**
 * Extract file path from public URL
 */
export function getFilePathFromUrl(url: string): string {
  const urlObj = new URL(url);
  const pathParts = urlObj.pathname.split('/');
  // Remove 'storage/v1/object/public/project-files/' prefix
  const bucketIndex = pathParts.indexOf(BUCKET_NAME);
  if (bucketIndex === -1) return '';
  return pathParts.slice(bucketIndex + 1).join('/');
}